import { createRoot } from 'react-dom/client';
import { uniq } from 'sling-calendar-sdk/utils';
import { isProduction } from '@sdk/config/appConfig';

import initSentryReact from '@web/common/utils/initSentryReact';

import getComponentDefinition from './getComponentDefinition';
import reactAngularBridge from './reactAngularBridge';

const basicBindings = ['<', '<?', '@', '@?', '=', '=?'];

const parseBindingsForAngular = (bindings) => {
  return Object.keys(bindings).reduce(
    (parsedBindings, bindingName) => ({
      ...parsedBindings,
      [bindingName]: bindings[bindingName],
    }),
    {}
  );
};

const getDefinition = (item, propsToBind, dependenciesToBind) => {
  if (item.ReactComponent) {
    const { ReactComponent, bindings, dependencies } = item;
    return {
      ReactComponent,
      bindings: bindings || {},
      dependencies: dependencies || [],
    };
  }
  return getComponentDefinition(item, propsToBind, dependenciesToBind);
};

export default function r2a(
  ReactComponentOrComponentDefinition,
  propsToBind = [],
  dependenciesToBind = []
) {
  const definition = getDefinition(
    ReactComponentOrComponentDefinition,
    propsToBind,
    dependenciesToBind
  );

  const dependencies = uniq(['$element', '$scope', '$attrs', 'Authentication', ...definition.dependencies]);

  class ReactInAngularComponentController {
    constructor(...injectedDependencies) {
      injectedDependencies.forEach((dependency, index) => {
        const dependencyName = dependencies[index];
        this[dependencyName] = dependency;

        if (dependencyName === 'Authentication') {
          this.Authentication = dependency;
        }
      });
    }

    $onInit() {
      const { ReactComponent } = definition;
      this.BridgedComponent = reactAngularBridge(ReactComponent);

      this.angularDependencies = this.getAngularDependencies();
      this.props = this.getComponentProps();
      this.attrs = this.getComponentAttrs();

      this.addWatchers();
      this.render();
    }

    $onChanges(changes) {
      const propsUpdated = Object.entries(changes).reduce((updates, [propName, propChange]) => {
        if (!propChange.isFirstChange()) {
          updates[propName] = propChange.currentValue;
        }
        return updates;
      }, {});

      if (Object.keys(propsUpdated).length) {
        this.props = {
          ...this.props,
          ...propsUpdated,
        };
        this.render();
      }
    }

    $onDestroy() {
      this.root.unmount();
      this.root = null;
      this.removeWatchers();
    }

    addWatchers() {
      this.watchers = [];
      Object.entries(definition.bindings).forEach(([bindingName, bindingType]) => {
        if (bindingType === '=') {
          const unwatch = this.$scope.$watch(
            `ctrl.${bindingName}`,
            (currentValue, previousValue) => {
              if (currentValue !== previousValue) {
                this.props[bindingName] = currentValue;
                this.render();
              }
            }
          );
          this.watchers.push(unwatch);
        }
      });
    }

    removeWatchers() {
      this.watchers.forEach((watcher) => watcher());
    }

    getAngularDependencies() {
      return definition.dependencies.reduce(
        (angularDeps, dependencyName) => ({
          ...angularDeps,
          [dependencyName]: this[dependencyName],
        }),
        {}
      );
    }

    getComponentProps() {
      return Object.entries(definition.bindings).reduce((props, [bindingName, bindingType]) => {
        if (basicBindings.includes(bindingType)) {
          return {
            ...props,
            [bindingName]: this[bindingName],
          };
        }

        if (bindingType === '&' || bindingType === '&?') {
          return {
            ...props,
            ...(bindingType === '&' || this[bindingName]
              ? {
                  [bindingName]: (locals) => {
                    const result = this[bindingName](locals);
                    this.$scope.$evalAsync();
                    return result;
                  },
                }
              : {}),
          };
        }

        return props;
      }, {});
    }

    getComponentAttrs() {
      const propKeys = Object.keys(this.props);
      return Object.keys(this.$attrs.$attr).reduce((htmlAttrs, attr) => {
        // Whatever is in props - we handled it explicity as component prop
        // Special case is 'class' as we don't want to pass it to react,
        // we want it to stay on component wrapper.
        if (!propKeys.includes(attr) && attr !== 'class') {
          // === '' check handles required attribute and similar.
          htmlAttrs[attr] = this.$attrs[attr] === '' ? true : this.$attrs[attr];
        }
        return htmlAttrs;
      }, {});
    }

    render() {
      const BridgedComponent = this.BridgedComponent;
      if (!this.root) {
        // Sentry is currently disabled in production because we were bumping up
        // against our error limits after adding it to sling web.
        const sentryEnabled = !(__TEST__ || isProduction);
        initSentryReact(sentryEnabled, this.Authentication?.user?.id, this.Authentication?.user?.org?.id);

        this.root = createRoot(this.$element[0]);
      }

      this.root.render(
        <BridgedComponent {...this.attrs} {...this.props} {...this.angularDependencies} />
      );
    }
  }

  return {
    bindings: parseBindingsForAngular(definition.bindings),
    controllerAs: 'ctrl',
    controller: [...dependencies, ReactInAngularComponentController],
  };
}
